import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useDashboard } from '../../context/DashboardContext';
import { t } from '../../utils/translations';
import { toast } from '../../hooks/useToast';
import { invokeBilling, pendingPaidAction } from '../../services/billingCheckout';
import { BILLING_CONFIRMED_EVENT, parseCheckoutReturn, stripCheckoutParams } from '../../utils/checkoutReturn';
import { planMetaFor } from '../../utils/planPricing';

// Retour de Stripe Checkout, sur n'importe quel écran : le paiement est CONFIRMÉ côté serveur
// (mode `confirm`, même livraison idempotente que le webhook), le forfait est relu, puis l'écran
// d'origine est prévenu (événement) pour relancer l'action payée. Sans ce composant, rien ne
// réagissait au retour : forfait inchangé jusqu'au prochain login, test payé jamais envoyé.
const RETRY_DELAYS_MS = [0, 1500, 3000, 5000, 8000];
const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
// Refus définitifs : inutile de réessayer.
const FINAL_CODES = new Set(['invalid-session', 'session-not-found', 'not-an-app-session', 'app-only', 'no-enterprise']);

export function CheckoutReturnHandler(): null {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshEnterprise } = useAuth();
  const { language } = useDashboard();
  // Langue lue au moment du toast (la confirmation peut durer ~20 s).
  const langRef = useRef(language || 'en');
  langRef.current = language || 'en';
  const tx = (key: string) => t(langRef.current, key);
  const handled = useRef<string | null>(null);

  useEffect(() => {
    const ret = parseCheckoutReturn(location.search);
    if (!ret || !user.loggedIn) return;
    const key = `${location.pathname}${location.search}`;
    if (handled.current === key) return;               // StrictMode / re-render : une seule fois
    handled.current = key;

    // Retire les paramètres Stripe de l'URL — seulement si l'utilisateur est resté sur l'écran de
    // retour (sinon on le ramènerait en arrière après ses essais de confirmation).
    const clean = () => {
      if (window.location.pathname !== location.pathname || !parseCheckoutReturn(window.location.search)) return;
      navigate({ pathname: location.pathname, search: stripCheckoutParams(window.location.search), hash: window.location.hash }, { replace: true });
    };

    if (ret.state === 'cancel') {
      pendingPaidAction.clear();
      toast.info(tx('billing.return.canceled'));
      clean();
      return;
    }
    if (!ret.sessionId) { clean(); return; }

    // Pas d'annulation au démontage/re-rendu : la garde `handled` empêche le doublon, et la
    // confirmation doit aller au bout même si l'écran change entre-temps.
    (async () => {
      for (const delay of RETRY_DELAYS_MS) {
        if (delay) await sleep(delay);
        const { data, code } = await invokeBilling('create-checkout-session', { mode: 'confirm', sessionId: ret.sessionId });
        if (code === 'not-authorized-for-billing' || code === 'forbidden') {
          // Le payeur n'est pas cette session : le webhook livrera, on relira le forfait plus tard.
          toast.info(tx('billing.return.receivedLater'));
          clean();
          return;
        }
        if (code && FINAL_CODES.has(code)) { clean(); return; }
        if (code) continue;                               // erreur passagère → nouvel essai
        if (data?.paid !== true) continue;               // banque pas encore confirmée

        await refreshEnterprise?.();
        if (data.kind === 'usage') pendingPaidAction.markConfirmed();
        window.dispatchEvent(new CustomEvent(BILLING_CONFIRMED_EVENT, { detail: data }));
        if (data.kind === 'subscription') {
          const label = data.tier ? planMetaFor(data.tier, langRef.current).label : '';
          toast.success(label ? tx('billing.return.planActiveFmt').replace('{plan}', label) : tx('billing.toast.syncConfirmed'));
        } else if (data.kind === 'usage') {
          toast.success(tx('billing.return.paymentConfirmed'));
        } else {
          toast.success(tx('billing.return.settled'));
        }
        clean();
        return;
      }
      toast.info(tx('billing.return.pendingBank'), 8000);
      clean();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname, user.loggedIn]);

  return null;
}
